import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Switch, Textarea } from '../../ui/components';
import { useCommercialSettings } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { ITEM_STATUSES } from './catalogApi';

const EMPTY = {
  name: '',
  sku: '',
  barcode: '',
  status: 'Active',
  categoryId: '',
  stockUomId: '',
  isStockTracked: true,
  taxRatePercent: '',
  description: '',
  tags: '',
  costAmount: '',
  costCurrency: '',
  defaultCostCenter: '',
  defaultGlAccountCode: '',
};

function fromItem(i, defaultCurrency) {
  if (!i) return { ...EMPTY, costCurrency: defaultCurrency };
  return {
    name: i.name ?? '',
    sku: i.sku ?? '',
    barcode: i.barcode ?? '',
    status: i.status ?? 'Active',
    categoryId: i.categoryId ?? '',
    stockUomId: i.stockUomId ?? '',
    isStockTracked: i.isStockTracked ?? true,
    taxRatePercent: i.taxRatePercent ?? '',
    description: i.description ?? '',
    tags: (i.tags ?? []).join(', '),
    costAmount: i.standardCost?.amount ?? '',
    costCurrency: i.standardCost?.currency ?? defaultCurrency,
    defaultCostCenter: i.defaultCostCenter ?? '',
    defaultGlAccountCode: i.defaultGlAccountCode ?? '',
  };
}

/**
 * Item create/edit form. `onSubmit(payload)` receives a cleaned payload; the
 * parent owns the API call, loading state and drawer.
 */
export default function ItemForm({ item, categories = [], units = [], costCenters = [], glAccounts = [], submitting, onSubmit, onCancel }) {
  const t = useT();
  const { currencies, defaultCurrency } = useCommercialSettings();
  const [form, setForm] = useState(() => fromItem(item, defaultCurrency));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(fromItem(item, defaultCurrency));
    setErrors({});
  }, [item, defaultCurrency]);

  const currencyOptions = useMemo(() => {
    const codes = [...currencies];
    if (form.costCurrency && !codes.includes(form.costCurrency)) codes.push(form.costCurrency);
    return codes.map((c) => ({ value: c, label: c }));
  }, [currencies, form.costCurrency]);

  const statusOptions = useMemo(() => ITEM_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })), [t]);
  const categoryOptions = useMemo(
    () => [{ value: '', label: t('— No category —') }, ...categories.map((c) => ({ value: c.id, label: c.name }))],
    [categories, t],
  );
  const unitOptions = useMemo(
    () => [{ value: '', label: t('— No unit —') }, ...units.map((u) => ({ value: u.id, label: `${u.name} (${u.code})` }))],
    [units, t],
  );
  const costCenterOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...costCenters.filter((c) => c.isActive !== false).map((c) => ({ value: c.code, label: c.name ? `${c.code} · ${c.name}` : c.code }))],
    [costCenters, t],
  );
  const glAccountOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...glAccounts.filter((g) => g.isActive !== false).map((g) => ({ value: g.code, label: g.name ? `${g.code} · ${g.name}` : g.code }))],
    [glAccounts, t],
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('Name is required.');
    if (form.taxRatePercent !== '' && (Number(form.taxRatePercent) < 0 || Number(form.taxRatePercent) > 100))
      next.taxRatePercent = t('Must be 0–100.');
    if (form.costAmount !== '' && Number(form.costAmount) < 0) next.costAmount = t('Must be ≥ 0.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const tags = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    onSubmit({
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      barcode: form.barcode.trim() || null,
      status: form.status,
      categoryId: form.categoryId || null,
      stockUomId: form.stockUomId || null,
      isStockTracked: form.isStockTracked,
      taxRatePercent: form.taxRatePercent === '' ? null : Number(form.taxRatePercent),
      description: form.description.trim() || null,
      tags,
      standardCost:
        form.costAmount === '' ? null : { amount: Number(form.costAmount), currency: (form.costCurrency || defaultCurrency).toUpperCase() },
      defaultCostCenter: form.defaultCostCenter || null,
      defaultGlAccountCode: form.defaultGlAccountCode || null,
    });
  };

  return (
    <form id="item-form" className="pp-form" onSubmit={handleSubmit}>
      <Input label={t('Name')} placeholder={t('House Red Wine 750ml')} value={form.name} onChange={set('name')} error={errors.name} autoFocus />

      <div className="pp-form__row">
        <Input label={t('SKU / code')} placeholder="WIN-RED-750" value={form.sku} onChange={set('sku')} />
        <Input label={t('Barcode')} placeholder="5901234123457" value={form.barcode} onChange={set('barcode')} />
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Status')}</span>
          <Select value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} options={statusOptions} ariaLabel={t('Status')} />
        </div>
        <div className="ps-field">
          <span className="ps-field__label">{t('Category')}</span>
          <Select value={form.categoryId} onChange={(v) => setForm((f) => ({ ...f, categoryId: v }))} options={categoryOptions} ariaLabel={t('Category')} searchable />
        </div>
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Stock unit')}</span>
          <Select value={form.stockUomId} onChange={(v) => setForm((f) => ({ ...f, stockUomId: v }))} options={unitOptions} ariaLabel={t('Stock unit')} searchable />
        </div>
        <Input label={t('Tax / VAT rate (%)')} type="number" placeholder="20" value={form.taxRatePercent} onChange={set('taxRatePercent')} error={errors.taxRatePercent} />
      </div>

      <div className="pp-form__row">
        <Input label={t('Standard cost')} type="number" placeholder="0.00" value={form.costAmount} onChange={set('costAmount')} error={errors.costAmount} />
        <div className="ps-field">
          <span className="ps-field__label">{t('Cost currency')}</span>
          <Select value={form.costCurrency} onChange={(v) => setForm((f) => ({ ...f, costCurrency: v }))} options={currencyOptions} ariaLabel={t('Cost currency')} />
        </div>
      </div>

      <div className="pp-form__row">
        <div className="ps-field">
          <span className="ps-field__label">{t('Default cost centre')}</span>
          <Select value={form.defaultCostCenter} onChange={(v) => setForm((f) => ({ ...f, defaultCostCenter: v }))} options={costCenterOptions} ariaLabel={t('Default cost centre')} searchable />
          <span className="ps-field__hint">{t('Pre-fills coding on requisition & PO lines for this item.')}</span>
        </div>
        <div className="ps-field">
          <span className="ps-field__label">{t('Default GL account')}</span>
          <Select value={form.defaultGlAccountCode} onChange={(v) => setForm((f) => ({ ...f, defaultGlAccountCode: v }))} options={glAccountOptions} ariaLabel={t('Default GL account')} searchable />
        </div>
      </div>

      <Switch
        checked={form.isStockTracked}
        onChange={(v) => setForm((f) => ({ ...f, isStockTracked: v }))}
        label={t('Track stock for this item')}
        hint={t("Turn off for services or direct-expense items that aren't held in inventory.")}
      />

      <Input label={t('Tags')} placeholder={t('beverage, wine, red (comma separated)')} value={form.tags} onChange={set('tags')} />

      <Textarea label={t('Description')} rows={3} placeholder={t('Optional notes about the item.')} value={form.description} onChange={set('description')} />

      <div className="pp-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          {t('Cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={submitting}>
          {item ? t('Save changes') : t('Create item')}
        </Button>
      </div>
    </form>
  );
}
